import { useState } from 'react';
import { UMR } from '../_components';
import { ProdSearchInput, ProdTag } from './ProdControls';
import { usePerimeterSearch } from './hooks';
import type { ExportPerimeter } from './types';

/**
 * "Périmètre" field of the export panel: production search input ("Rechercher une ville")
 * with a suggestion list, and the chosen commune as a removable blue `fluid-tag` below.
 */
export interface PerimeterFieldProps {
  value: ExportPerimeter | null;
  onChange: (perimeter: ExportPerimeter | null) => void;
}

export function PerimeterField({ value, onChange }: PerimeterFieldProps) {
  const [query, setQuery] = useState('');
  const { data, isFetching, isError } = usePerimeterSearch(query);
  const showList = query.trim().length >= 2;

  const pick = (p: ExportPerimeter) => {
    onChange(p);
    setQuery('');
  };

  const status = (text: string) => <div style={{ padding: '6px 16px', fontSize: 12, lineHeight: UMR.lhXs, color: UMR.textSecondary }}>{text}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ position: 'relative' }}>
        <ProdSearchInput value={query} onChange={setQuery} placeholder="Rechercher une ville" />
        {showList && (
          <div
            role="listbox"
            aria-label="Communes"
            style={{ position: 'absolute', top: 32, left: 0, right: 0, zIndex: 5, background: UMR.white, border: `1px solid ${UMR.border}`, boxShadow: '0 4px 8px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}
          >
            {isFetching && !data && status('Recherche en cours…')}
            {isError && status('Recherche indisponible, réessayez.')}
            {data && data.length === 0 && status('Aucune commune trouvée')}
            {data?.map((p) => (
              <div
                key={p.id}
                role="option"
                aria-selected={value?.id === p.id}
                tabIndex={0}
                className="um-hover-grey"
                onClick={() => pick(p)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && pick(p)}
                style={{ padding: '6px 16px', cursor: 'pointer', fontSize: 14, lineHeight: '20px' }}
              >
                <div>{p.label}</div>
                <div style={{ fontSize: 12, lineHeight: UMR.lhXs, color: UMR.textSecondary }}>{p.department} · {p.inseeCode}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {value && (
        <div style={{ display: 'flex' }}>
          <ProdTag onRemove={() => onChange(null)} removeLabel={`Retirer ${value.label}`}>{value.label}</ProdTag>
        </div>
      )}
    </div>
  );
}
